import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { PageHeader } from "../components/PageHeader";
import { EmptyState, InlineError, LoadingState } from "../components/States";
import { useToast } from "../components/Toast";
import { adminApi } from "../lib/api";
import type { MediaRecord, MediaUploadPayload } from "../types";

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPTED_TYPES = new Set(["image/webp", "image/jpeg", "image/png"]);

function normalizeMedia(payload: unknown): MediaRecord[] {
  const list = Array.isArray(payload)
    ? payload
    : payload && typeof payload === "object"
      ? Array.isArray((payload as Record<string, unknown>).items)
        ? ((payload as Record<string, unknown>).items as unknown[])
        : Array.isArray((payload as Record<string, unknown>).media)
          ? ((payload as Record<string, unknown>).media as unknown[])
          : Array.isArray((payload as Record<string, unknown>).data)
            ? ((payload as Record<string, unknown>).data as unknown[])
            : []
      : [];
  return list.map((item, index) => {
    const record =
      item && typeof item === "object" ? (item as Record<string, unknown>) : {};
    return {
      ...record,
      id: String(record.id || record.url || record.filename || index)
    };
  });
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      if (comma < 0) {
        reject(new Error("The selected image could not be encoded."));
        return;
      }
      resolve(result.slice(comma + 1));
    };
    reader.onerror = () => reject(new Error("The selected image could not be read."));
    reader.readAsDataURL(file);
  });
}

function formatBytes(bytes: unknown): string {
  if (typeof bytes !== "number") return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function MediaPage() {
  const { showToast } = useToast();
  const fileInput = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<MediaRecord[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [altEn, setAltEn] = useState("");
  const [altTr, setAltTr] = useState("");
  const [source, setSource] = useState("");
  const [license, setLicense] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setItems(normalizeMedia(await adminApi.media()));
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "The media library could not be loaded."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function validateFile(nextFile: File | null): string {
    if (!nextFile) return "Choose an image to upload.";
    if (!ACCEPTED_TYPES.has(nextFile.type)) {
      return "Use a WebP, JPEG, or PNG image.";
    }
    if (nextFile.size > MAX_BYTES) {
      return "The image must be 5 MB or smaller.";
    }
    return "";
  }

  async function upload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validation = validateFile(file);
    if (validation) {
      setFormError(validation);
      return;
    }
    if (!altEn.trim() || !altTr.trim() || !source.trim() || !license.trim()) {
      setFormError("English alt text, Turkish alt text, source, and license are required.");
      return;
    }

    setUploading(true);
    setFormError("");
    try {
      const selected = file!;
      const payload: MediaUploadPayload = {
        filename: selected.name,
        contentType: selected.type,
        data: await fileToBase64(selected),
        altEn: altEn.trim(),
        altTr: altTr.trim(),
        source: source.trim(),
        license: license.trim()
      };
      const created = await adminApi.uploadMedia(payload);
      if (created && typeof created === "object" && created.id) {
        setItems((current) => [created, ...current]);
      } else {
        await load();
      }
      setFile(null);
      setAltEn("");
      setAltTr("");
      setSource("");
      setLicense("");
      if (fileInput.current) fileInput.current.value = "";
      showToast("Image added to the media library.", "success");
    } catch (requestError) {
      setFormError(
        requestError instanceof Error
          ? requestError.message
          : "The image could not be uploaded."
      );
    } finally {
      setUploading(false);
    }
  }

  async function copyUrl(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      showToast("Media URL copied.", "success");
    } catch {
      showToast("Copy failed. Select the URL from the media details.", "error");
    }
  }

  async function deleteItem(item: MediaRecord) {
    if (
      !window.confirm(
        `Delete “${item.filename || item.id}”? Existing website references may stop displaying this image.`
      )
    ) {
      return;
    }
    setDeletingId(item.id);
    try {
      await adminApi.deleteMedia(item.id);
      setItems((current) => current.filter((candidate) => candidate.id !== item.id));
      showToast("Image deleted.", "success");
    } catch (requestError) {
      showToast(
        requestError instanceof Error
          ? requestError.message
          : "The image could not be deleted.",
        "error"
      );
    } finally {
      setDeletingId("");
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Website assets"
        title="Media"
        description="Upload compact web images with complete bilingual accessibility and rights metadata."
        actions={
          <button type="button" className="button" onClick={() => void load()}>
            Refresh
          </button>
        }
      />

      <section className="media-upload panel" aria-labelledby="upload-title">
        <div className="panel__heading">
          <div>
            <p className="eyebrow">New asset</p>
            <h2 id="upload-title">Upload an image</h2>
          </div>
          <span>WebP, JPEG, PNG · max 5 MB</span>
        </div>
        <form onSubmit={upload}>
          <div className="field media-upload__file">
            <label htmlFor="media-file">Image file</label>
            <input
              ref={fileInput}
              id="media-file"
              type="file"
              accept=".webp,.jpg,.jpeg,.png,image/webp,image/jpeg,image/png"
              required
              onChange={(event) => {
                const nextFile = event.target.files?.[0] || null;
                setFile(nextFile);
                setFormError(validateFile(nextFile));
              }}
            />
            {file && (
              <p className="field__help">
                {file.name} · {formatBytes(file.size)}
              </p>
            )}
          </div>
          <div className="localized-grid">
            <div className="field">
              <label htmlFor="media-alt-en">English alt text</label>
              <textarea
                id="media-alt-en"
                rows={3}
                required
                value={altEn}
                onChange={(event) => setAltEn(event.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="media-alt-tr">Türkçe alternatif metin</label>
              <textarea
                id="media-alt-tr"
                rows={3}
                required
                value={altTr}
                onChange={(event) => setAltTr(event.target.value)}
              />
            </div>
          </div>
          <div className="media-upload__meta">
            <div className="field">
              <label htmlFor="media-source">Source / creator</label>
              <input
                id="media-source"
                required
                value={source}
                onChange={(event) => setSource(event.target.value)}
                placeholder="ATALAY commissioned image"
              />
            </div>
            <div className="field">
              <label htmlFor="media-license">License / usage rights</label>
              <input
                id="media-license"
                required
                value={license}
                onChange={(event) => setLicense(event.target.value)}
                placeholder="Owned by ATALAY"
              />
            </div>
          </div>
          {formError && (
            <p className="field__error" role="alert">
              {formError}
            </p>
          )}
          <button
            type="submit"
            className="button button--primary"
            disabled={uploading}
          >
            {uploading ? "Uploading…" : "Upload image"}
          </button>
        </form>
      </section>

      <section className="media-library" aria-labelledby="library-title">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Library</p>
            <h2 id="library-title">Available images</h2>
          </div>
          <span>{items.length} assets</span>
        </div>
        {loading ? (
          <LoadingState label="Loading media" />
        ) : error ? (
          <InlineError message={error} retry={() => void load()} />
        ) : items.length === 0 ? (
          <EmptyState
            title="The media library is empty"
            message="Upload the first website image using the form above."
          />
        ) : (
          <div className="media-grid">
            {items.map((item) => (
              <article className="media-card" key={item.id}>
                <div className="media-card__image">
                  {item.url ? (
                    <img
                      src={item.url}
                      alt={item.altEn || item.filename || ""}
                      loading="lazy"
                    />
                  ) : (
                    <span>No preview URL</span>
                  )}
                </div>
                <div className="media-card__body">
                  <h3>{item.filename || item.id}</h3>
                  {item.url && <code>{item.url}</code>}
                  <dl>
                    <div>
                      <dt>EN alt</dt>
                      <dd>{item.altEn || "—"}</dd>
                    </div>
                    <div>
                      <dt>TR alt</dt>
                      <dd>{item.altTr || "—"}</dd>
                    </div>
                    <div>
                      <dt>Source</dt>
                      <dd>{item.source || "—"}</dd>
                    </div>
                    <div>
                      <dt>License</dt>
                      <dd>{item.license || "—"}</dd>
                    </div>
                  </dl>
                  <div className="media-card__actions">
                    <button
                      type="button"
                      className="button button--small"
                      disabled={!item.url}
                      onClick={() => item.url && void copyUrl(item.url)}
                    >
                      Copy URL
                    </button>
                    <button
                      type="button"
                      className="button button--small button--danger"
                      disabled={deletingId === item.id}
                      onClick={() => void deleteItem(item)}
                    >
                      {deletingId === item.id ? "Deleting…" : "Delete"}
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
